import { useState, useEffect, useCallback } from 'react';

import {
  Chart as ChartJS,
  LinearScale,
  LineElement,
  Tooltip,
  TimeSeriesScale,
  BarElement,
  PointElement,
  CategoryScale,
  type ChartOptions,
} from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { Bar, Line, Chart } from 'react-chartjs-2';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';
import { useFrontendTool } from '@copilotkit/react-core';
import { Card, Stack, Table, Text, Title } from '@mantine/core';

import { ChartType, DataWidget } from 'types/dashboard_widget';

import { DataWidgetFiltersInput, FilterInput } from 'dashboards/types/filter';
import { TimeSeriesGranularity } from 'dashboards/types/time';
import { TIME_UNIT_MAP } from 'dashboards/types/time_unit';

import { resolveGranularity } from 'dashboards/services/granularity';
import { HEATMAP_CHART_GRANULARITIES } from 'dashboards/services/heatmap';
import { hslaToString, MAIN_CHART_COLOR } from 'dashboards/services/color';
import { useChartColors } from 'dashboards/hooks/useChartColors';
import { useCounter } from 'dashboards/hooks/useCounter';
import { useHeatmapChart } from 'dashboards/hooks/useHeatmapChart';
import { useLeaderboard } from 'dashboards/hooks/useLeaderboard';
import { useTimeSeries } from 'dashboards/hooks/useTimeSeries';

import { ChartError } from 'dashboards/components/ChartError/ChartError';
import { ChartLoader } from 'dashboards/components/ChartLoader/ChartLoader';

import { AddToDashboardMenu } from './AddToDashboardMenu';
import { ChartTypeSwitcher } from './ChartTypeSwitcher';

ChartJS.register(
  BarElement,
  CategoryScale,
  LineElement,
  LinearScale,
  MatrixController,
  MatrixElement,
  PointElement,
  Tooltip,
  TimeSeriesScale
);

type QueryType = 'counter' | 'timeseries' | 'leaderboard';

type QueryAnalyticsArgs = {
  queryType: QueryType;
  metricName: string;
  timeRangeStart: string;
  timeRangeStop: string;
  granularity?: TimeSeriesGranularity;
  dimensions?: string;
  stacked?: boolean;
  rowLimit?: number;
  sort?: 'ASC' | 'DESC';
  filters?: string;
};

type QueryAnalyticsRenderProps = {
  status: 'complete' | 'executing' | 'inProgress';
  args: QueryAnalyticsArgs;
};

function CounterRenderer({
  metricName,
  timeRangeStart,
  timeRangeStop,
  filters,
}: {
  metricName: string;
  timeRangeStart: string;
  timeRangeStop: string;
  filters: FilterInput[];
}) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const enabled = Boolean(metricName && timeRangeStart && timeRangeStop);

  const { data, isLoading, error } = useCounter({
    params: {
      metricName,
      timeRange: { start: timeRangeStart, stop: timeRangeStop },
      timeZone: timezone,
      filters,
    },
    enabled,
  });

  if (error) {
    return <ChartError />;
  }

  if (isLoading || !enabled) {
    return <ChartLoader />;
  }

  if (!data) {
    return <ChartError error="No data available." />;
  }

  const value = Number(data.value);

  return (
    <div className="flex h-full w-full items-center justify-center py-4">
      <span className="text-4xl font-semibold text-neutral-900 dark:text-neutral-100">
        {!isNaN(value) ? Math.round(value).toLocaleString() : (data.value ?? '-')}
      </span>
    </div>
  );
}

function TimeSeriesRenderer({
  metricName,
  timeRangeStart,
  timeRangeStop,
  granularity: rawGranularity,
  dimensions,
  stacked = false,
  filters,
  useLineChart: forceLineChart,
}: {
  metricName: string;
  timeRangeStart: string;
  timeRangeStop: string;
  granularity: TimeSeriesGranularity;
  dimensions: string[];
  stacked?: boolean;
  filters: FilterInput[];
  useLineChart?: boolean;
}) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const granularity = resolveGranularity(rawGranularity, {
    start: timeRangeStart,
    stop: timeRangeStop,
  });

  const enabled = Boolean(metricName && timeRangeStart && timeRangeStop);

  const { data, isLoading, error } = useTimeSeries({
    params: {
      metricName,
      timeRange: { start: timeRangeStart, stop: timeRangeStop },
      timeZone: timezone,
      granularity,
      dimensions: dimensions.length > 0
        ? dimensions.map((columnName) => ({ columnName }))
        : undefined,
      filters,
    },
    enabled,
  });

  // Get colors for dimension values
  const dimensionLabels = data && data.length > 1
    ? data.map((series) => {
        // Get dimension values from series (exclude labels and values keys)
        const dimValues = Object.entries(series)
          .filter(([key]) => key !== 'labels' && key !== 'values')
          .map(([, value]) => value)
          .join(' - ');
        return dimValues || 'Unknown';
      })
    : undefined;
  const { data: colors, isLoading: isLoadingColors } = useChartColors(dimensionLabels);

  const timeUnit = TIME_UNIT_MAP[granularity];

  const hasDimensions = data && data.length > 1;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'timeseries' as const,
        time: timeUnit ? { unit: timeUnit } : undefined,
        adapters: {
          date: {
            zone: timezone,
          },
        },
        stacked: hasDimensions && stacked,
      },
      y: {
        beginAtZero: true,
        stacked: hasDimensions && stacked,
      },
    },
    plugins: {
      legend: {
        display: hasDimensions,
        position: 'bottom' as const,
      },
    },
  };

  if (error) {
    return <ChartError />;
  }

  if (isLoading || !enabled || (hasDimensions && isLoadingColors)) {
    return <ChartLoader />;
  }

  if (!data || data.length === 0) {
    return <ChartError error="No data available for this time range." />;
  }

  // Use bar chart for shorter time ranges, line for longer (or forced via prop)
  const useLineChart = forceLineChart ?? data[0].values.length > 30;

  // Build datasets - single series or multiple for dimensions
  const labels = data[0].labels;
  const datasets = data.length === 1
    ? [
        {
          ...(useLineChart ? {} : { borderRadius: 2 }),
          backgroundColor: hslaToString(MAIN_CHART_COLOR),
          borderColor: hslaToString(MAIN_CHART_COLOR),
          data: data[0].values,
        },
      ]
    : data.map((series, index) => {
        // Get dimension values for label
        const dimValues = Object.entries(series)
          .filter(([key]) => key !== 'labels' && key !== 'values')
          .map(([, value]) => value)
          .join(' - ');

        const color = colors?.[index]
          ? hslaToString(colors[index])
          : hslaToString(MAIN_CHART_COLOR);

        return {
          label: dimValues || `Series ${index + 1}`,
          ...(useLineChart ? {} : { borderRadius: 2 }),
          backgroundColor: color,
          borderColor: color,
          data: series.values,
        };
      });

  return (
    <div className="h-48">
      {useLineChart ? (
        <Line options={options} data={{ labels, datasets }} />
      ) : (
        <Bar options={options} data={{ labels, datasets }} />
      )}
    </div>
  );
}

function HeatmapRenderer({
  metricName,
  timeRangeStart,
  timeRangeStop,
  granularity: rawGranularity,
  dimensions,
  filters,
}: {
  metricName: string;
  timeRangeStart: string;
  timeRangeStop: string;
  granularity: TimeSeriesGranularity;
  dimensions: string[];
  filters: FilterInput[];
}) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const granularity = resolveGranularity(
    rawGranularity,
    { start: timeRangeStart, stop: timeRangeStop },
    HEATMAP_CHART_GRANULARITIES
  );

  const enabled = Boolean(metricName && timeRangeStart && timeRangeStop && dimensions.length > 0);

  const { seriesLabels, data, isLoading, error } = useHeatmapChart({
    metricName,
    timeRange: { start: timeRangeStart, stop: timeRangeStop },
    timeZone: timezone,
    granularity,
    dimensions: dimensions.map((columnName) => ({ columnName })),
    filters,
  });

  if (error) {
    return <ChartError error={error.message} />;
  }

  if (isLoading || !enabled) {
    return <ChartLoader />;
  }

  if (!data || !seriesLabels) {
    return <ChartError error="No data available for this time range." />;
  }

  const options: ChartOptions<'matrix'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'category' as const,
        labels: data.labels,
        offset: true,
        ticks: { display: true },
        grid: { display: false },
      },
      y: {
        type: 'category' as const,
        labels: seriesLabels,
        offset: true,
        ticks: { display: true },
        grid: { display: false },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        callbacks: {
          label(context) {
            const dataPoint = context.dataset.data[context.dataIndex];
            if (dataPoint && 'v' in dataPoint) {
              return [`${dataPoint.y}: ${dataPoint.v}`];
            }
            return [];
          },
        },
      },
    },
  };

  return (
    <div className="h-48">
      <Chart type="matrix" data={data} options={options} />
    </div>
  );
}

function LeaderboardRenderer({
  metricName,
  timeRangeStart,
  timeRangeStop,
  dimensions,
  rowLimit,
  sort,
  filters,
}: {
  metricName: string;
  timeRangeStart: string;
  timeRangeStop: string;
  dimensions: string[];
  rowLimit: number;
  sort: 'ASC' | 'DESC';
  filters: FilterInput[];
}) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const enabled = Boolean(metricName && timeRangeStart && timeRangeStop && dimensions.length > 0);

  const { data, isLoading, error } = useLeaderboard({
    params: {
      metricName,
      timeRange: { start: timeRangeStart, stop: timeRangeStop },
      timeZone: timezone,
      dimensions: dimensions.map((columnName) => ({ columnName })),
      rowLimit,
      sort,
      filters,
    },
    enabled,
  });

  // Use first label to determine bar color
  const colorLabels = data?.rows.map(([label]) => label);
  const { data: colors, isLoading: isLoadingColors } =
    useChartColors(colorLabels);

  if (error) {
    return <ChartError />;
  }

  if (isLoading || isLoadingColors || !enabled) {
    return <ChartLoader />;
  }

  if (!data || !colors) {
    return <ChartError error="No data available." />;
  }

  const { headers, rows } = data;

  if (rows.length === 0) {
    return <ChartError error="No data available for this time range." />;
  }

  // Index of the value column (always the last column)
  const valueIndex = headers.length - 1;

  const labels = rows.map((row) => row.slice(0, valueIndex).join(' - '));
  const values = rows.map((row) => parseInt(row[valueIndex], 10));

  const datasets = [
    {
      label: metricName,
      data: values,
      backgroundColor: colors.map(hslaToString),
      borderColor: colors.map(hslaToString),
    },
  ];

  return (
    <div className="h-48">
      <Bar
        options={{
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: {
            legend: {
              display: false,
            },
          },
        }}
        data={{ labels, datasets }}
      />
    </div>
  );
}

function LeaderboardTableRenderer({
  metricName,
  timeRangeStart,
  timeRangeStop,
  dimensions,
  rowLimit,
  sort,
  filters,
}: {
  metricName: string;
  timeRangeStart: string;
  timeRangeStop: string;
  dimensions: string[];
  rowLimit: number;
  sort: 'ASC' | 'DESC';
  filters: FilterInput[];
}) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const enabled = Boolean(metricName && timeRangeStart && timeRangeStop && dimensions.length > 0);

  const { data, isLoading, error } = useLeaderboard({
    params: {
      metricName,
      timeRange: { start: timeRangeStart, stop: timeRangeStop },
      timeZone: timezone,
      dimensions: dimensions.map((columnName) => ({ columnName })),
      rowLimit,
      sort,
      filters,
    },
    enabled,
  });

  if (error) {
    return <ChartError />;
  }

  if (isLoading || !enabled) {
    return <ChartLoader />;
  }

  if (!data) {
    return <ChartError error="No data available." />;
  }

  const { headers, rows } = data;

  if (rows.length === 0) {
    return <ChartError error="No data available for this time range." />;
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          {headers.map((header) => (
            <Table.Th key={header}>{header}</Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row, index) => (
          <Table.Tr key={index}>
            {row.map((cell, cellIndex) => (
              <Table.Td key={cellIndex}>
                {cellIndex === headers.length - 1
                  ? Number(cell).toLocaleString()
                  : cell}
              </Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function safeJsonParse<T>(json: string | T | undefined, fallback: T): T {
  if (!json) return fallback;
  // If already the expected type (array/object), return directly
  if (typeof json !== 'string') return json;
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

function QueryAnalyticsRenderer({ status, args }: QueryAnalyticsRenderProps) {
  const {
    queryType,
    metricName,
    timeRangeStart,
    timeRangeStop,
    granularity = 'AUTO',
    dimensions,
    stacked = false,
    rowLimit = 10,
    sort = 'DESC',
    filters: filtersJson,
  } = args;

  const filters: FilterInput[] = safeJsonParse(filtersJson, []);
  const dimensionsArray: string[] = safeJsonParse(dimensions, []);

  // Determine default chart type based on query type and dimensions
  const getDefaultChartType = useCallback((): ChartType => {
    switch (queryType) {
      case 'counter':
        return 'counter';
      case 'timeseries':
        if (dimensionsArray.length > 0) {
          return stacked ? 'timeseries-bar-stacked' : 'timeseries-bar-grouped';
        }
        return 'timeseries-bar';
      case 'leaderboard':
        return rowLimit > 5 ? 'leaderboard-table' : 'leaderboard-bar';
      default:
        return 'timeseries-bar';
    }
  }, [queryType, dimensionsArray.length, stacked, rowLimit]);

  const [selectedChartType, setSelectedChartType] = useState<ChartType | null>(null);

  // Set chart type once query args are available
  useEffect(() => {
    if (status === 'complete' && queryType && selectedChartType === null) {
      setSelectedChartType(getDefaultChartType());
    }
  }, [status, queryType, selectedChartType, getDefaultChartType]);

  // Convert FilterInput to DataWidgetFiltersInput (adds id field)
  const dataWidgetFilters = filters.map((f: FilterInput) => ({
    ...f,
    id: crypto.randomUUID(),
  })) as DataWidgetFiltersInput[];

  const createWidget = () => {
    const defaultLayout = { x: 0, y: 0, w: 4, h: 3 };

    let query: DataWidget<ChartType>['query'];

    // Build query based on the selected chart type
    if (selectedChartType === 'counter') {
      query = {
        metric: metricName,
        filters: dataWidgetFilters.length > 0 ? dataWidgetFilters : undefined,
      };
    } else if (
      selectedChartType === 'timeseries-bar' ||
      selectedChartType === 'timeseries-line'
    ) {
      query = {
        metric: metricName,
        granularity,
        filters: dataWidgetFilters.length > 0 ? dataWidgetFilters : undefined,
      };
    } else if (
      selectedChartType === 'timeseries-bar-stacked' ||
      selectedChartType === 'timeseries-bar-grouped' ||
      selectedChartType === 'timeseries-heatmap'
    ) {
      query = {
        metric: metricName,
        granularity,
        dimensions: dimensionsArray.map((columnName) => ({ columnName })),
        filters: dataWidgetFilters.length > 0 ? dataWidgetFilters : undefined,
      };
    } else if (
      selectedChartType === 'leaderboard-bar' ||
      selectedChartType === 'leaderboard-table'
    ) {
      query = {
        metric: metricName,
        dimensions: dimensionsArray.map((columnName) => ({ columnName })),
        rowLimit,
        sort,
        filters: dataWidgetFilters.length > 0 ? dataWidgetFilters : undefined,
      };
    } else {
      query = { metric: metricName };
    }

    return new DataWidget(
      crypto.randomUUID(),
      metricName,
      selectedChartType as ChartType,
      query as DataWidget<ChartType>['query'],
      defaultLayout
    );
  };

  if (status === 'inProgress' || status === 'executing' || selectedChartType === null) {
    return (
      <Card withBorder p="md" radius="md">
        <ChartLoader />
      </Card>
    );
  }

  const renderContent = () => {
    // Render based on selected chart type for switching
    switch (selectedChartType) {
      case 'counter':
        return (
          <CounterRenderer
            metricName={metricName}
            timeRangeStart={timeRangeStart}
            timeRangeStop={timeRangeStop}
            filters={filters}
          />
        );
      case 'timeseries-bar':
      case 'timeseries-line':
        return (
          <TimeSeriesRenderer
            metricName={metricName}
            timeRangeStart={timeRangeStart}
            timeRangeStop={timeRangeStop}
            granularity={granularity}
            dimensions={[]}
            stacked={false}
            filters={filters}
            useLineChart={selectedChartType === 'timeseries-line'}
          />
        );
      case 'timeseries-bar-grouped':
        return (
          <TimeSeriesRenderer
            metricName={metricName}
            timeRangeStart={timeRangeStart}
            timeRangeStop={timeRangeStop}
            granularity={granularity}
            dimensions={dimensionsArray}
            stacked={false}
            filters={filters}
          />
        );
      case 'timeseries-bar-stacked':
        return (
          <TimeSeriesRenderer
            metricName={metricName}
            timeRangeStart={timeRangeStart}
            timeRangeStop={timeRangeStop}
            granularity={granularity}
            dimensions={dimensionsArray}
            stacked={true}
            filters={filters}
          />
        );
      case 'timeseries-heatmap':
        return (
          <HeatmapRenderer
            metricName={metricName}
            timeRangeStart={timeRangeStart}
            timeRangeStop={timeRangeStop}
            granularity={granularity}
            dimensions={dimensionsArray}
            filters={filters}
          />
        );
      case 'leaderboard-bar':
        return (
          <LeaderboardRenderer
            metricName={metricName}
            timeRangeStart={timeRangeStart}
            timeRangeStop={timeRangeStop}
            dimensions={dimensionsArray}
            rowLimit={rowLimit}
            sort={sort}
            filters={filters}
          />
        );
      case 'leaderboard-table':
        return (
          <LeaderboardTableRenderer
            metricName={metricName}
            timeRangeStart={timeRangeStart}
            timeRangeStop={timeRangeStop}
            dimensions={dimensionsArray}
            rowLimit={rowLimit}
            sort={sort}
            filters={filters}
          />
        );
      default:
        return <Text c="dimmed">Unknown chart type: {selectedChartType}</Text>;
    }
  };

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatDateTime = (iso: string) =>
    DateTime.fromISO(iso).setZone(timezone).toLocaleString(DateTime.DATETIME_SHORT);

  return (
    <Card withBorder p="md" radius="md" className="relative">
      <Stack gap="sm">
        <Title order={5}>{metricName}</Title>
        <Text size="xs" c="dimmed">
          {formatDateTime(timeRangeStart)} → {formatDateTime(timeRangeStop)} ({timezone})
        </Text>
        {renderContent()}
      </Stack>
      <div className="absolute bottom-2 right-2 flex items-center gap-1">
        <ChartTypeSwitcher
          queryType={queryType}
          hasDimensions={dimensionsArray.length > 0}
          currentChartType={selectedChartType}
          onChartTypeChange={setSelectedChartType}
        />
        <AddToDashboardMenu createWidget={createWidget} />
      </div>
    </Card>
  );
}

export function useQueryAnalyticsTool() {
  const now = new Date().toISOString();

  useFrontendTool(
    {
      name: 'queryAnalytics',
      description: `Query analytics data to answer questions about metrics. Always use listAvailableMetrics tool first to discover metric names and their available dimensions. Supports three query types: "counter" for single aggregate values, "timeseries" for trends over time, and "leaderboard" for top N rankings. IMPORTANT: When user asks for data "by", "per", "broken down by", or "grouped by" something (e.g., "by camera", "per source", "by object type"), you MUST use the dimensions parameter with the appropriate dimension name from listAvailableMetrics (e.g., '["source"]', '["camera_id"]', '["object_class"]'). When choosing dimensions, ALWAYS prefer human-readable name fields over ID fields for better chart readability (e.g., use "gateway_name" instead of "gateway_id", "camera_name" instead of "camera_id", "source" instead of "source_id", "deployment_name" instead of "deployment_id"). CRITICAL: ALWAYS default to queryType "timeseries" unless the user explicitly asks for a single total/count number (use "counter") or explicitly asks for a ranking/top N list (use "leaderboard"). Use a last-24-hours time range by default unless otherwise specified. Current date/time: ${now}.`,
      parameters: [
        {
          name: 'queryType',
          type: 'string',
          description:
            'Type of query: "counter" for single value, "timeseries" for trend chart, "leaderboard" for rankings',
          required: true,
        },
        {
          name: 'metricName',
          type: 'string',
          description:
            'The name of the metric to query (e.g., "object_count", "event_count")',
          required: true,
        },
        {
          name: 'timeRangeStart',
          type: 'string',
          description: 'Start of time range in ISO 8601 format (e.g., "2024-01-01T00:00:00Z")',
          required: true,
        },
        {
          name: 'timeRangeStop',
          type: 'string',
          description: 'End of time range in ISO 8601 format (e.g., "2024-01-31T23:59:59Z")',
          required: true,
        },
        {
          name: 'granularity',
          type: 'string',
          description:
            'Time granularity for timeseries queries: "AUTO", "MINUTE", "FIVE_MINUTES", "TEN_MINUTES", "FIFTEEN_MINUTES", "HOUR", "DAY", "WEEK", "MONTH", "YEAR". Defaults to "AUTO" which picks granularity based on the time range.',
          required: false,
        },
        {
          name: 'dimensions',
          type: 'string',
          description:
            'REQUIRED when user wants data "broken down by", "grouped by", "per", or "by" something. JSON array of dimension column names (e.g., \'["source"]\', \'["camera_id"]\', \'["object_class"]\'). Use listAvailableMetrics to see available dimensions for each metric. For timeseries: creates grouped/stacked bar chart. For leaderboard: required for ranking.',
          required: false,
        },
        {
          name: 'stacked',
          type: 'boolean',
          description: 'For timeseries with dimensions: if true, shows stacked bars; if false (default), shows grouped bars side by side.',
          required: false,
        },
        {
          name: 'rowLimit',
          type: 'number',
          description: 'Maximum number of rows for leaderboard queries. Defaults to 10.',
          required: false,
        },
        {
          name: 'sort',
          type: 'string',
          description: 'Sort order for leaderboard: "ASC" or "DESC". Defaults to "DESC".',
          required: false,
        },
        {
          name: 'filters',
          type: 'string',
          description:
            'JSON array of filter objects with {column, operator, value} for filtering data',
          required: false,
        },
      ],
      handler: async (args: { queryType: string; metricName: string; timeRangeStart: string; timeRangeStop: string; granularity: string | undefined; dimensions: string | undefined; stacked: boolean | undefined; rowLimit: number | undefined; sort: string | undefined; filters: string | undefined; }) => {
        return { ...args, message: 'Analytics query displayed to user' };
      },
      render: ({ status, args }) => (
        <QueryAnalyticsRenderer
          status={status}
          args={args as QueryAnalyticsArgs}
        />
      ),
    },
    []
  );
}
